/**
 * Detección y recuperación de ChunkLoadError (deploy nuevo, red, Clerk CDN).
 */

const ADMIN_CHUNK_RETRY_KEY = "admin:chunk-recovery:v1";
const ADMIN_CHUNK_RETRY_TTL_MS = 2 * 60 * 1000;

export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  const name = err instanceof Error ? err.name : "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

export function isClerkChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return isChunkLoadError(err) && /clerk\./i.test(msg);
}

/** True if this navigation already used ?_cb= (auto-retry already ran). */
export function hasCacheBustParam(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URL(window.location.href).searchParams.has("_cb");
  } catch {
    return false;
  }
}

export function canAutoRetryChunkRecovery(): boolean {
  if (typeof window === "undefined") return false;
  // Avoid infinite reload loops when _cb already present.
  if (hasCacheBustParam()) return false;
  try {
    const raw = sessionStorage.getItem(ADMIN_CHUNK_RETRY_KEY);
    if (!raw) return true;
    const at = Number(raw);
    if (!Number.isFinite(at)) return true;
    return Date.now() - at > ADMIN_CHUNK_RETRY_TTL_MS;
  } catch {
    return true;
  }
}

export function markChunkRecoveryAttempt(): void {
  try {
    sessionStorage.setItem(ADMIN_CHUNK_RETRY_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearChunkRecoveryAttempt(): void {
  try {
    sessionStorage.removeItem(ADMIN_CHUNK_RETRY_KEY);
  } catch {
    /* ignore */
  }
}

export function reloadWithCacheBust(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set("_cb", String(Date.now()));
  window.location.replace(url.toString());
}

/**
 * Hard recovery: drop recovery lock, bust query, try Cache API clear for /assets.
 * Use after a failed auto-retry so the user gets a clean shell+chunks load.
 */
export async function hardReloadAdminShell(): Promise<void> {
  if (typeof window === "undefined") return;
  clearChunkRecoveryAttempt();
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore */
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_cb", String(Date.now()));
  // Full navigation (not only replace) to drop in-memory module graph.
  window.location.href = url.toString();
}

/**
 * Retry a dynamic import once after a short delay (stale deploy race).
 */
export function importWithRetry<T>(
  loader: () => Promise<T>,
  retries = 1,
  delayMs = 400,
): Promise<T> {
  return loader().catch(async (err) => {
    if (retries <= 0 || !isChunkLoadError(err)) throw err;
    await new Promise((r) => setTimeout(r, delayMs));
    return importWithRetry(loader, retries - 1, delayMs * 1.5);
  });
}
